/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import {
  Map as MapIcon,
  Users,
  Activity,
  AlertTriangle,
  X,
  Search,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  FileText,
  Menu,
  ChevronLeft,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
  Tooltip,
} from "react-leaflet";
import { AdminContext } from "../../../context/AdminContext";
import ProfileModal from "../vetting/ProfileModal";
import { SUBCITY_COORDS } from "../../../utils/constants";

import "leaflet/dist/leaflet.css";
import placeholderImage from "../../../assets/placeholder.webp";

const AddisCenter = [9.0192, 38.7525];

// Component to control map focus
const MapFocus = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 13, { animate: true });
  }, [center, map]);
  return null;
};

const Coverage = () => {
  const { coverageData, fetchCoverageData } = useContext(AdminContext);
  const [selectedSubcity, setSelectedSubcity] = useState(null);
  const [viewMode, setViewMode] = useState("all");
  const [hoveredSubcity, setHoveredSubcity] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCaregivers, setFilteredCaregivers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Initialize data structure with real data
  const [subcitiesData, setSubcitiesData] = useState([]);

  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidePanelOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Fetch coverage data on component mount
    const params = {};
    if (viewMode === "available") {
      params.available = "true";
    }
    fetchCoverageData(params);
  }, [fetchCoverageData, viewMode]);

  // Process coverage data when it changes
  useEffect(() => {
    if (coverageData && Object.keys(coverageData).length > 0) {
      const processedSubcities = Object.keys(SUBCITY_COORDS).map(
        (subcityName) => {
          const subcityData = coverageData[subcityName] || {
            total: 0,
            available: 0,
            caregivers: [],
          };

          // Process caregivers to ensure they have all necessary fields
          const processedCaregivers = (subcityData.caregivers || []).map(
            (cg) => ({
              ...cg,
              // Ensure all fields are properly set
              _id: cg.id,
              id: cg.id,
              name: cg.name || "Unknown Caregiver",
              email: cg.email || "N/A",
              phoneNumber: cg.phoneNumber || "N/A",
              alternativephoneNumber: cg.alternativephoneNumber || "N/A",
              service:
                cg.serviceCategory?.label ||
                cg.service ||
                cg.category ||
                "Caregiver",
              category: cg.serviceCategory?.label || cg.category || "Caregiver",
              available: cg.available || false,
              rating: cg.rating || 0,
              image:
                cg.image ||
                `https://ui-avatars.com/api/?name=${
                  cg.name || "Caregiver"
                }&background=CE257A&color=fff`,
              experience: cg.experience || "Not specified",
              about:
                cg.about ||
                `${cg.name} is a dedicated caregiver specializing in ${
                  cg.serviceCategory?.label || cg.category || "care services"
                }.`,
              languages: cg.languages || ["English"],
              date_time_preference_to_work:
                cg.date_time_preference_to_work || "Flexible",
              workPreference: cg.date_time_preference_to_work || "Flexible",
              address: cg.address || {
                line1: cg.location || "Address not specified",
                line2: cg.subcity || "Not specified",
              },
              fees: cg.fees || "Negotiable",
              vetting_status: cg.vetting_status || "Onboarded",
              vettingHistory: cg.vettingHistory || [],
              subcity: cg.subcity || "Not specified",
              gender: cg.gender || "Not specified",
              dob: cg.dob || "Not specified",
              education: cg.education || "Not specified",
              nationality: cg.nationality || "Not specified",
              createdAt: cg.createdAt || new Date().toISOString(),
              updatedAt: cg.updatedAt || new Date().toISOString(),
            }),
          );

          return {
            id: subcityName,
            name: subcityName,
            coordinates: SUBCITY_COORDS[subcityName],
            total: subcityData.total || 0,
            active: subcityData.total || 0,
            available: subcityData.available || 0,
            caregivers: processedCaregivers,
            coverageRate:
              subcityData.total > 0
                ? Math.round((subcityData.available / subcityData.total) * 100)
                : 0,
          };
        },
      );
      setSubcitiesData(processedSubcities);
    }
  }, [coverageData]);

  // Update filtered caregivers when selected subcity changes
  useEffect(() => {
    if (selectedSubcity) {
      const caregivers = selectedSubcity.caregivers || [];
      const filtered = searchTerm
        ? caregivers.filter(
            (cg) =>
              cg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (cg.serviceCategory?.label &&
                cg.serviceCategory.label
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) ||
              (cg.service &&
                cg.service.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (cg.category &&
                cg.category.toLowerCase().includes(searchTerm.toLowerCase())),
          )
        : caregivers;
      setFilteredCaregivers(filtered);
    }
  }, [selectedSubcity, searchTerm]);

  // Helper to determine marker color based on your color scheme
  const getMarkerColor = (subcity) => {
    const value = viewMode === "all" ? subcity.total : subcity.available;

    if (value === 0) return "#000000"; // Black for no caregivers

    // Determine if underserved (less than 10 caregivers)
    if (subcity.total < 10 || subcity.coverageRate < 30) return "#CE257A";

    // Scale based on density
    const maxValue = Math.max(
      ...subcitiesData.map((s) => (viewMode === "all" ? s.total : s.available)),
    );
    const density = value / maxValue;

    // Return gradient from light to dark pink based on density
    if (density < 0.3) return "rgba(206, 37, 122, 0.5)";
    if (density < 0.6) return "rgba(206, 37, 122, 0.7)";
    return "rgba(206, 37, 122, 0.9)";
  };

  // Calculate marker size
  const getMarkerRadius = (subcity) => {
    const value = viewMode === "all" ? subcity.total : subcity.available;
    if (value === 0) return 8;
    return 8 + Math.min(value / 5, 15); // Scale radius based on count
  };

  // Calculate totals
  const totalCaregivers = subcitiesData.reduce(
    (acc, curr) => acc + curr.total,
    0,
  );
  const totalAvailable = subcitiesData.reduce(
    (acc, curr) => acc + curr.available,
    0,
  );
  const underservedSubcities = subcitiesData.filter(
    (s) => s.total === 0 || s.coverageRate < 30 || s.total < 8,
  );

  // Handle subcity selection
  const handleSelectSubcity = (subcity) => {
    setSelectedSubcity(subcity);
    setSearchTerm("");
    if (isMobileView) {
      setIsSidePanelOpen(true);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedSubcity(null);
    setIsSidePanelOpen(false);
  };

  // Get coverage stats for a subcity
  const getCoverageStats = (subcity) => {
    if (!subcity) return { status: "empty", label: "No Data" };

    if (subcity.total === 0) return { status: "empty", label: "No Caregivers" };

    const coverageRate = subcity.coverageRate;
    if (coverageRate >= 50)
      return {
        status: "good",
        label: "Good Coverage",
        icon: <TrendingUp size={12} />,
      };
    if (coverageRate >= 20)
      return {
        status: "fair",
        label: "Fair Coverage",
        icon: <TrendingUp size={12} />,
      };
    return {
      status: "low",
      label: "Low Coverage",
      icon: <TrendingDown size={12} />,
    };
  };

  // Handle caregiver click to show profile
  const handleCaregiverClick = (caregiver) => {
    // Format caregiver data to match ProfileModal expectations
    const formattedCaregiver = {
      ...caregiver,
      // Ensure ID fields are consistent
      _id: caregiver.id,
      id: caregiver.id,

      // Basic info
      name: caregiver.name || "Unknown Caregiver",
      category:
        caregiver.serviceCategory?.label ||
        caregiver.category ||
        caregiver.service ||
        "Caregiver",
      rating: parseFloat(caregiver.rating) || 0,
      fees: caregiver.fees || "Negotiable",

      // Contact info
      phoneNumber: caregiver.phoneNumber || "N/A",
      email: caregiver.email || "N/A",
      alternativephoneNumber: caregiver.alternativephoneNumber || "N/A",

      // Location
      address: caregiver.address || {
        line1: caregiver.location || "Not specified",
        line2: caregiver.subcity || "Not specified",
      },
      subcity: caregiver.subcity || "Not specified",
      location: caregiver.location || "Not specified",

      // Professional details
      experience: caregiver.experience || "Not specified",
      about:
        caregiver.about ||
        `${caregiver.name} is a dedicated caregiver specializing in ${
          caregiver.serviceCategory?.label ||
          caregiver.category ||
          caregiver.service ||
          "care services"
        }.`,
      languages: Array.isArray(caregiver.languages)
        ? caregiver.languages
        : typeof caregiver.languages === "string"
          ? caregiver.languages.split(",")
          : ["English"],
      date_time_preference_to_work:
        caregiver.date_time_preference_to_work ||
        caregiver.workPreference ||
        "Flexible",

      // Personal details
      gender: caregiver.gender || "Not specified",
      dob: caregiver.dob || "Not specified",
      education: caregiver.education || "Not specified",
      nationality: caregiver.nationality || "Not specified",

      // Vetting info
      vetting_status: caregiver.vetting_status || "Onboarded",
      vettingHistory: caregiver.vettingHistory || [],

      // Image
      image: caregiver.image || placeholderImage,

      // Timestamps
      createdAt: caregiver.createdAt,
      updatedAt: caregiver.updatedAt,
    };

    setSelectedCaregiver(formattedCaregiver);
    setIsProfileModalOpen(true);
  };

  // Close profile modal
  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedCaregiver(null);
  };

  // Close side panel on mobile
  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedSubcity(null);
  };

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Mobile Header */}
      {isMobileView && selectedSubcity && isSidePanelOpen && (
        <div className="md:hidden bg-card dark:bg-dark-surface px-4 py-3 border-b border-gray-100 dark:border-dark-border flex items-center justify-between">
          <button
            onClick={handleCloseSidePanel}
            className="p-2 text-text dark:text-dark-text"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 className="text-lg font-bold text-text dark:text-dark-text truncate">
            {selectedSubcity.name}
          </h2>
          <div className="w-8"></div> {/* Spacer for alignment */}
        </div>
      )}

      {/* Header Stats - Responsive */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-6 mb-4 md:mb-6 px-4 md:px-0">
        <div className="bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl border border-slate-800 shadow-sm flex-1 flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-wider">
              Citywide Coverage
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mt-1">
              {totalCaregivers}
            </h2>
            <p className="text-xs text-primary font-bold mt-1 flex items-center gap-1">
              <Users size={10} /> Across {subcitiesData.length} Subcities
            </p>
          </div>
          <MapIcon size={32} className="text-primary md:w-10 md:h-10" />
        </div>

        <div className="bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl border border-slate-800 shadow-sm flex-1 flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-wider">
              Available Now
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mt-1">
              {totalAvailable}
            </h2>
            <p className="text-xs text-primary font-bold mt-1 flex items-center gap-1">
              <Activity size={10} />{" "}
              {Math.round((totalAvailable / totalCaregivers) * 100) || 0}%
              Availability
            </p>
          </div>
          <Users size={32} className="text-primary md:w-10 md:h-10" />
        </div>

        {/* Underserved Areas Card with Tooltip */}
        <div className="bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl border border-slate-800 shadow-sm flex-1 flex items-center justify-between relative group">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-wider">
                Underserved Areas
              </p>
              <div className="relative cursor-help">
                <AlertTriangle size={12} className="text-gray-400" />

                {/* Simple Tooltip - Now appears below */}
                <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 min-w-max">
                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                    <div className="font-bold mb-1">
                      What are underserved areas?
                    </div>
                    <div>Areas with low caregiver coverage or availability</div>
                    <div className="mt-1 text-gray-300">
                      • &lt;30% available caregivers
                      <br />• &lt;8 total caregivers
                    </div>
                  </div>

                  {/* Tooltip arrow - pointing up now */}
                  <div className="absolute left-3 bottom-full w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-extrabold text-white mt-1">
              {underservedSubcities.length}
            </h2>
            <p className="text-xs text-primary font-bold mt-1 flex items-center gap-1">
              <AlertTriangle size={10} /> Needs Attention
            </p>
          </div>
          <AlertTriangle size={32} className="text-primary md:w-10 md:h-10" />
        </div>
      </div>

      {/* View Mode Toggle - Responsive */}
      <div className="mb-4 md:mb-6 px-4 md:px-0">
        <div className="bg-card dark:bg-dark-surface p-1 rounded-lg md:rounded-xl border border-gray-100 dark:border-dark-border shadow-sm inline-flex w-full md:w-auto">
          <button
            onClick={() => handleViewModeChange("all")}
            className={`px-4 md:px-6 py-2 text-sm font-bold rounded-lg transition-all flex-1 md:flex-none ${
              viewMode === "all"
                ? "bg-primary text-white"
                : "text-text dark:text-dark-muted hover:bg-accent dark:hover:bg-dark-border"
            }`}
          >
            All Caregivers
          </button>
          <button
            onClick={() => handleViewModeChange("available")}
            className={`px-4 md:px-6 py-2 text-sm font-bold rounded-lg transition-all flex-1 md:flex-none ${
              viewMode === "available"
                ? "bg-primary text-white"
                : "text-text dark:text-dark-muted hover:bg-accent dark:hover:bg-dark-border"
            }`}
          >
            Available Now
          </button>
        </div>
      </div>

      {/* Main Interactive Area */}
      <div className="flex-1 flex gap-4 md:gap-6 overflow-hidden relative px-4 md:px-0 md:pr-4 md:pl-4">
        {/* Map Canvas */}
        <div
          className={`${isMobileView && isSidePanelOpen ? "hidden" : "flex-1"} bg-card dark:bg-dark-surface rounded-2xl md:rounded-3xl border border-gray-100 dark:border-dark-border shadow-sm relative overflow-hidden`}
        >
          <MapContainer
            center={AddisCenter}
            zoom={isMobileView ? 11 : 12}
            scrollWheelZoom={false}
            className="h-full w-full rounded-2xl md:rounded-3xl"
            style={{ borderRadius: isMobileView ? "16px" : "24px", zIndex: 1 }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {selectedSubcity &&
              selectedSubcity.coordinates &&
              !isMobileView && (
                <MapFocus center={selectedSubcity.coordinates} />
              )}

            {subcitiesData.map((subcity) => {
              if (!subcity.coordinates) return null;

              const isSelected = selectedSubcity?.id === subcity.id;
              const stats = getCoverageStats(subcity);

              return (
                <CircleMarker
                  key={subcity.id}
                  center={subcity.coordinates}
                  radius={getMarkerRadius(subcity)}
                  pathOptions={{
                    color: "#FFFFFF",
                    fillColor: getMarkerColor(subcity),
                    fillOpacity: isSelected ? 0.9 : 0.8,
                    weight: isSelected ? 3 : 2,
                  }}
                  eventHandlers={{
                    click: () => handleSelectSubcity(subcity),
                    mouseover: () => setHoveredSubcity(subcity.id),
                    mouseout: () => setHoveredSubcity(null),
                  }}
                >
                  <Tooltip
                    direction="top"
                    offset={[0, -10]}
                    opacity={1}
                    permanent={false}
                    className="leaflet-tooltip-custom hidden md:block"
                  >
                    <div className="bg-dark-surface/95 text-dark-text text-[10px] py-2 px-3 rounded-xl shadow-xl backdrop-blur-sm border border-dark-border">
                      <div className="font-bold mb-1 border-b border-dark-border pb-1">
                        {subcity.name}
                      </div>
                      <div className="flex justify-between gap-2 text-[9px] mt-1">
                        <span className="text-dark-muted">Total</span>
                        <span className="font-bold">{subcity.total}</span>
                      </div>
                      <div className="flex justify-between gap-2 text-[9px]">
                        <span className="text-dark-muted">Available</span>
                        <span className="font-bold text-primary">
                          {subcity.available}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2 text-[9px] mt-1">
                        <span className="text-dark-muted">Coverage</span>
                        <span
                          className={`font-bold ${
                            stats.status === "good"
                              ? "text-green-500"
                              : stats.status === "fair"
                                ? "text-yellow-500"
                                : "text-red-500"
                          }`}
                        >
                          {stats.label}
                        </span>
                      </div>
                    </div>
                  </Tooltip>

                  <Popup className="md:hidden">
                    <div className="p-3">
                      <h3 className="font-bold text-base mb-3">
                        {subcity.name}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Total Caregivers:
                          </span>
                          <span className="font-bold">{subcity.total}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Available Now:</span>
                          <span className="font-bold text-primary">
                            {subcity.available}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Coverage Rate:</span>
                          <span className="font-bold">
                            {subcity.coverageRate}%
                          </span>
                        </div>
                        <div
                          className={`mt-3 text-sm px-3 py-2 rounded-lg text-center font-bold ${
                            stats.status === "good"
                              ? "bg-green-100 text-green-700"
                              : stats.status === "fair"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {stats.label}
                        </div>
                      </div>
                      <button
                        onClick={() => handleSelectSubcity(subcity)}
                        className="mt-4 w-full bg-primary text-white text-sm py-2.5 px-4 rounded-xl hover:bg-primary/90 transition-colors font-bold"
                      >
                        View Details
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Map Legend - Mobile: Hidden, Desktop: Bottom left */}
          <div className="absolute bottom-4 left-4 bg-card/90 dark:bg-dark-surface/90 backdrop-blur-sm p-3 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm z-20 hidden md:block">
            <p className="text-xs font-bold mb-2 text-text dark:text-dark-text">
              Coverage Legend
            </p>
            <div className="space-y-1.5 text-[10px] text-text dark:text-dark-muted">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black border border-gray-700"></div>
                <span>No Caregivers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary/50 border border-primary/50"></div>
                <span>Low Density</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary/70 border border-primary"></div>
                <span>Medium Density</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary border border-primary"></div>
                <span>High Density</span>
              </div>
            </div>
          </div>

          {/* Mobile Map Info Overlay */}
          {isMobileView && !isSidePanelOpen && selectedSubcity && (
            <div className="absolute top-4 left-4 right-4 bg-card/90 dark:bg-dark-surface/90 backdrop-blur-sm p-3 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm z-20">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-text dark:text-dark-text">
                  {selectedSubcity.name}
                </h3>
                <button
                  onClick={() => setSelectedSubcity(null)}
                  className="p-1 hover:bg-accent dark:hover:bg-dark-border rounded-full"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-background dark:bg-dark-surface p-2 rounded-lg">
                  <p className="text-[10px] text-gray-500 dark:text-dark-muted">
                    Total
                  </p>
                  <p className="font-bold text-sm">{selectedSubcity.total}</p>
                </div>
                <div className="bg-background dark:bg-dark-surface p-2 rounded-lg">
                  <p className="text-[10px] text-gray-500 dark:text-dark-muted">
                    Available
                  </p>
                  <p className="font-bold text-sm text-primary">
                    {selectedSubcity.available}
                  </p>
                </div>
                <div className="bg-background dark:bg-dark-surface p-2 rounded-lg">
                  <p className="text-[10px] text-gray-500 dark:text-dark-muted">
                    Coverage
                  </p>
                  <p className="font-bold text-sm">
                    {selectedSubcity.coverageRate}%
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsSidePanelOpen(true)}
                className="mt-3 w-full bg-primary text-white text-xs py-2 rounded-lg hover:bg-primary/90 transition-colors font-bold"
              >
                View Caregivers
              </button>
            </div>
          )}
        </div>

        {/* Side Detail Panel */}
        <div
          className={`${
            isMobileView
              ? isSidePanelOpen
                ? "fixed inset-0 z-50 bg-card dark:bg-dark-surface"
                : "hidden"
              : "w-80 lg:w-96"
          } bg-card dark:bg-dark-surface rounded-2xl md:rounded-3xl border border-gray-100 dark:border-dark-border shadow-xl transition-all duration-300 flex flex-col z-30 ${
            (selectedSubcity && !isMobileView) ||
            (isMobileView && isSidePanelOpen)
              ? ""
              : "opacity-0 pointer-events-none"
          }`}
          style={{ zIndex: 30 }}
        >
          {selectedSubcity ? (
            <>
              {/* Mobile Header for Side Panel */}
              {isMobileView && (
                <div className="md:hidden p-4 border-b border-gray-100 dark:border-dark-border flex items-center justify-between bg-accent dark:bg-dark-surface">
                  <h2 className="text-xl font-bold text-text dark:text-dark-text">
                    {selectedSubcity.name}
                  </h2>
                  <button
                    onClick={handleCloseSidePanel}
                    className="p-2 hover:bg-accent dark:hover:bg-dark-border rounded-full transition-colors text-text dark:text-dark-muted"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}

              {/* Desktop Header */}
              {!isMobileView && (
                <div className="p-4 md:p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-start bg-accent dark:bg-dark-surface rounded-t-2xl md:rounded-t-3xl">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-xl md:text-2xl font-bold text-text dark:text-dark-text">
                        {selectedSubcity.name}
                      </h2>
                      <span
                        className={`px-2 py-1 rounded-md text-xs font-bold ${
                          getCoverageStats(selectedSubcity).status === "good"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : getCoverageStats(selectedSubcity).status ===
                                "fair"
                              ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        }`}
                      >
                        {getCoverageStats(selectedSubcity).label}
                      </span>
                    </div>
                    <div className="flex gap-2 md:gap-3">
                      <div className="bg-card dark:bg-dark-surface px-2 md:px-3 py-1.5 rounded-lg border border-gray-100 dark:border-dark-border flex-1">
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          Total
                        </p>
                        <p className="text-base md:text-lg font-bold text-text dark:text-dark-text">
                          {selectedSubcity.total}
                        </p>
                      </div>
                      <div className="bg-card dark:bg-dark-surface px-2 md:px-3 py-1.5 rounded-lg border border-gray-100 dark:border-dark-border flex-1">
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          Available
                        </p>
                        <p className="text-base md:text-lg font-bold text-primary">
                          {selectedSubcity.available}
                        </p>
                      </div>
                      <div className="bg-card dark:bg-dark-surface px-2 md:px-3 py-1.5 rounded-lg border border-gray-100 dark:border-dark-border flex-1">
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          Coverage
                        </p>
                        <p className="text-base md:text-lg font-bold text-text dark:text-dark-text">
                          {selectedSubcity.coverageRate}%
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedSubcity(null)}
                    className="p-2 hover:bg-accent dark:hover:bg-dark-border rounded-full transition-colors text-text dark:text-dark-muted ml-2"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}

              {/* Search Bar */}
              <div className="p-3 md:p-4 border-b border-gray-100 dark:border-dark-border">
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search caregivers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-accent dark:bg-dark-border border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
                  />
                </div>
              </div>

              {/* Caregivers List */}
              <div className="flex-1 overflow-y-auto p-3 md:p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-bold text-text dark:text-dark-text">
                    Caregivers ({filteredCaregivers.length})
                  </h3>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="text-xs text-primary hover:underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>

                {filteredCaregivers.length > 0 ? (
                  <div className="space-y-3">
                    {filteredCaregivers.map((caregiver) => (
                      <div
                        key={caregiver.id}
                        onClick={() => handleCaregiverClick(caregiver)}
                        className="group p-3 bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border rounded-xl md:rounded-2xl hover:bg-accent dark:hover:bg-dark-border transition-all duration-300 cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          <img
                            src={caregiver.image || placeholderImage}
                            alt={caregiver.name}
                            className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl object-cover border-2 border-white dark:border-dark-surface"
                            onError={(e) => {
                              e.target.src = placeholderImage;
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <h4 className="font-bold text-text dark:text-dark-text text-sm truncate">
                                {caregiver.name}
                              </h4>
                              <div className="flex items-center gap-1">
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                    caregiver.available
                                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                      : "bg-gray-100 text-gray-500 dark:bg-dark-border dark:text-dark-muted"
                                  }`}
                                >
                                  {caregiver.available ? "Available" : "Booked"}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCaregiverClick(caregiver);
                                  }}
                                  className="p-1 hover:bg-accent dark:hover:bg-dark-border rounded text-gray-400 dark:text-dark-muted"
                                  title="View Profile"
                                >
                                  <FileText size={12} />
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                              {caregiver.serviceCategory?.label ||
                                caregiver.category ||
                                caregiver.service ||
                                "Caregiver"}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-1 text-xs text-text dark:text-dark-muted">
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <span
                                      key={i}
                                      className={`${
                                        i <
                                        Math.floor(
                                          Number(caregiver.rating) || 0,
                                        )
                                          ? "text-yellow-500"
                                          : "text-gray-300 dark:text-gray-600"
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <span className="ml-1 text-[10px]">
                                  ({Number(caregiver.rating || 0).toFixed(1)})
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {caregiver.fees &&
                                  caregiver.fees !== "Negotiable" && (
                                    <span className="text-xs text-primary font-bold">
                                      Br {caregiver.fees}
                                    </span>
                                  )}
                                <ChevronRight
                                  size={14}
                                  className="text-gray-400 group-hover:text-primary transition-colors"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent dark:bg-dark-border flex items-center justify-center mx-auto mb-3">
                      <Users
                        size={20}
                        className="text-gray-400 dark:text-dark-muted"
                      />
                    </div>
                    <p className="text-gray-500 dark:text-dark-muted font-medium text-sm">
                      {searchTerm
                        ? "No caregivers match your search"
                        : "No caregivers in this area"}
                    </p>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="mt-2 text-sm text-primary hover:underline"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            !isMobileView && (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-dark-muted p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-accent dark:bg-dark-border flex items-center justify-center mb-4">
                  <MapIcon size={32} className="opacity-50" />
                </div>
                <p className="font-medium">
                  Select a subcity on the map to view detailed coverage and
                  caregiver lists.
                </p>
                <p className="text-sm mt-2 text-gray-500 dark:text-dark-muted">
                  Click on any marker to explore
                </p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobileView && !isSidePanelOpen && !selectedSubcity && (
        <div className="md:hidden px-4 pb-4">
          <div className="bg-card dark:bg-dark-surface p-4 rounded-2xl border border-gray-100 dark:border-dark-border text-center">
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              Tap on any marker to view subcity details and caregiver lists
            </p>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        caregiver={selectedCaregiver}
      />
    </div>
  );
};

export default Coverage;
